import React, { useCallback, useEffect, useState } from 'react';
import { VideoPlayer } from '../VideoPlayer/VideoPlayer';
import { Popup } from '../Popup/Popup';
import { Icon } from '../Layout/Icon/Icon';
import { Roleplay } from '../Roleplay/Roleplay';
import { Description } from './components/Description';
import { ControlButton } from './components/ControlButton';
import { preRoleplayDataTypes } from './types';
import { useTranslation } from 'react-i18next';
import axios, { AxiosResponse } from 'axios';
import { useSettings } from '../../context/Settings';
import { useParams } from 'react-router-dom';

export const PreRoleplay: React.FunctionComponent = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [rolePlaysData, setRoleplaysData] = useState<preRoleplayDataTypes[] | null>(null);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number>(0);
  const [playPressed, setPlayPressed] = useState<boolean>(false);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [hearStoryCanPlay, setHearStoryCanPlay] = useState<boolean>(false);
  const [isConversationActive, setConversationActive] = useState<boolean>(false);
  const [waitingVidCanPlay, setwaitingVidCanPlay] = useState(false);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayIndex, setDisplayIndex] = useState<number>(0);
  const [videoKey, setVideoKey] = useState<number>(0);

  const { t, i18n } = useTranslation();
  const { settings, saveSettings } = useSettings();
  const { lang: urlLang } = useParams<{ lang: string }>();
  const language = settings.language || urlLang;
  const buttonOverrideStyle = settings.isSafari ? { marginTop: '2px' } : {};

  useEffect(() => {
    if (!language) return;
    const fetchRoleplaysData = async () => {
      const { data }: AxiosResponse<preRoleplayDataTypes[]> = await axios.get(`/api/pre-roleplay?language=${language}`);
      setRoleplaysData(data);
    };

    fetchRoleplaysData();
  }, [language]);

  useEffect(() => {
    setPlayPressed(false);
    setActiveVideoIndex(0);
  }, [activeIndex]);

  useEffect(() => {
    if (activeIndex !== displayIndex && !playPressed) {
      setIsTransitioning(true);

      setwaitingVidCanPlay(false);

      const timeout = setTimeout(() => {
        setDisplayIndex(activeIndex);
        setVideoKey((prev) => prev + 1);

        setTimeout(() => {
          setIsTransitioning(false);
        }, 100);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [activeIndex, displayIndex, playPressed]);

  useEffect(() => {
    if (!playPressed) {
      setHearStoryCanPlay(false);
    }
    saveSettings({ showNav: !playPressed });
  }, [playPressed, saveSettings]);

  const onRoleplayFinish = useCallback(() => {
    setwaitingVidCanPlay(false);
    setIsTransitioning(false);
    setHearStoryCanPlay(false);
    setPlayPressed(false);
    setActiveVideoIndex(0);

    setVideoKey((prev) => prev + 1);

    setConversationActive(false);
    // Reset watermark context when leaving roleplay (EN only)
    if ((settings.language || urlLang) === 'en') {
      saveSettings({ watermarkContext: null });
    }
  }, []);

  // Ensure watermark context resets when unmounting this page
  useEffect(() => {
    return () => {
      if ((settings.language || urlLang) === 'en') {
        saveSettings({ watermarkContext: null });
      }
    };
  }, [settings.language, urlLang, saveSettings]);

  useEffect(() => {
    if (!isConversationActive && rolePlaysData) {
      const timeout = setTimeout(() => {}, 100);

      return () => clearTimeout(timeout);
    }
  }, [isConversationActive, rolePlaysData, waitingVidCanPlay, isTransitioning, displayIndex]);

  if (rolePlaysData === null) return <></>;

  const roleplay = rolePlaysData[displayIndex];
  const activeRoleplay = rolePlaysData[activeIndex];

  return (
    <>
      {isConversationActive ? (
        <Roleplay
          activeRoleplayID={activeRoleplay.id}
          setConversationActive={setConversationActive}
          onRoleplayFinish={onRoleplayFinish}
        />
      ) : (
        <>
          <div className="pre-roleplay">
            <div className={`pre-roleplay__about ${playPressed ? 'pre-roleplay__about--played' : ''}`}>
              {!playPressed && (
                <>
                  <div className="pre-roleplay__column pre-roleplay__column--left">
                    {activeIndex !== 0 && language !== 'nl' && (
                      <ControlButton
                        onButtonClick={() => setActiveIndex(activeIndex - 1)}
                        buttonClassName="column-controls__button--left"
                        ariaLabel={t('icons.previous')}
                      />
                    )}
                  </div>

                  <div className="about-btn-wrapper">
                    <button
                      className="button about-person__button about-person__button--mobile"
                      onClick={() => {
                        setPlayPressed(true);
                        setActiveVideoIndex(1);
                        if (i18n.language === 'en') {
                          saveSettings({ watermarkContext: 'hear_my_story' });
                        }
                      }}
                    >
                      <span style={buttonOverrideStyle}>{activeRoleplay.story}</span>
                      <Icon name="play" size={24} fill="#1E223C" />
                    </button>
                  </div>

                  <div className="pre-roleplay__column">
                    {activeIndex !== rolePlaysData.length - 1 && language !== 'nl' && (
                      <ControlButton
                        onButtonClick={() => setActiveIndex(activeIndex + 1)}
                        ariaLabel={t('icons.next')}
                      />
                    )}
                  </div>
                </>
              )}

              <div
                className={`about-person ${playPressed ? 'played-video' : 'default-video'} ${
                  waitingVidCanPlay && !isTransitioning ? 'default-video--visible' : ''
                }`}
              >
                <VideoPlayer
                  key={`waiting-${videoKey}`}
                  accountId={'3845398857001'}
                  videoId={roleplay.videoIds[0]}
                  videoUrl={roleplay.videoUrls[0]}
                  loop={true}
                  unmuted={false}
                  onCanPlay={() => setwaitingVidCanPlay(true)}
                />
              </div>

              {playPressed && (
                <div
                  className={`about-person played-video hear-story-vid ${
                    hearStoryCanPlay ? 'hear-story-vid--show' : ''
                  }`}
                >
                  <VideoPlayer
                    key={`story-${videoKey}`}
                    accountId={'3845398857001'}
                    videoId={roleplay.videoIds[1]}
                    videoUrl={roleplay.videoUrls[1]}
                    loop={false}
                    unmuted={true}
                    onCanPlay={() => setHearStoryCanPlay(true)}
                  />
                </div>
              )}
            </div>

            <div className="pre-roleplay__container">
              <Description
                data={rolePlaysData}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                activeVideo={activeVideoIndex}
                playPressed={playPressed}
                setActiveVideo={setActiveVideoIndex}
                setPlayPressed={setPlayPressed}
                setIsPopupOpen={setIsPopupOpen}
                setConversationActive={setConversationActive}
              />
            </div>
          </div>
          {isPopupOpen && (
            <Popup
              onCloseClick={() => {
                setIsPopupOpen(false);
                if ((settings.language || urlLang) === 'en') {
                  saveSettings({ watermarkContext: null });
                }
              }}
              trainingVideoID={activeRoleplay.trainingVideoID}
              trainingVideoUrl={activeRoleplay.trainingVideoUrl}
            />
          )}
        </>
      )}
    </>
  );
};
